import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowRight } from 'lucide-react';
import type { ReactElement } from 'react';

import { Icon } from '@/ui/kit';
import { controlHeight, fontWeights, radius } from '@/ui/tokens';

/**
 * Bouton d'envoi des ecrans de connexion : 56 px, degrade, puce a fleche.
 *
 * Il n'est pas le bouton du kit (40 px, a-plat) : la maquette « Q » en dessine un
 * seul, plus grand, et il ne sert que ces trois ecrans. **Le degrade commence a
 * `primary`**, et non au rose plus clair de la maquette (`#DC4566`, 4,13:1 sous
 * du texte blanc) : il finit sur `accentPinkStrong`, le texte tient donc 4,5:1
 * sur toute sa longueur (`tokens.contrast.test.ts`), en clair comme en sombre.
 *
 * Pendant l'envoi le bouton reste dans l'arbre, desactive et annonce
 * `aria-busy` : le retirer ferait perdre le focus a qui valide au clavier.
 */
export interface AuthSubmitButtonProps {
  readonly children: string;
  /** Envoi en cours : le bouton est inactif et la puce devient un indicateur. */
  readonly busy?: boolean;
  readonly disabled?: boolean;
}

export const AuthSubmitButton = ({ children, busy = false, disabled = false }: AuthSubmitButtonProps): ReactElement => (
  <ButtonBase
    type="submit"
    disabled={disabled || busy}
    aria-busy={busy}
    sx={(theme) => ({
      width: '100%',
      height: controlHeight.L,
      pl: '20px',
      pr: '8px',
      borderRadius: `${String(radius.field)}px`,
      justifyContent: 'space-between',
      fontFamily: 'inherit',
      fontSize: 16,
      fontWeight: fontWeights.extraBold,
      color: 'tokens.textOnPrimary',
      backgroundImage: `linear-gradient(90deg, ${theme.palette.tokens.primary} 0%, ${theme.palette.tokens.accentPinkStrong} 100%)`,
      boxShadow: `inset 0 1px 0 ${alpha(theme.palette.tokens.textOnPrimary, 0.25)}, 0 10px 20px -8px ${alpha(theme.palette.tokens.primary, 0.4)}`,
      '&:hover:not(.Mui-disabled)': { filter: 'brightness(0.95)' },
      // Un envoi en cours garde son aspect : seul un bouton sans saisie s'eteint.
      '&.Mui-disabled': busy
        ? { cursor: 'progress' }
        : {
            backgroundImage: 'none',
            bgcolor: 'tokens.surfaceMuted',
            color: 'tokens.textMuted',
            boxShadow: 'none',
            border: 1,
            borderColor: 'tokens.border',
          },
    })}
  >
    {/* Espaceur de la largeur de la puce : le libelle reste centre. */}
    <Box component="span" aria-hidden sx={{ width: 28 }} />
    <Box component="span">{children}</Box>
    <Box
      component="span"
      aria-hidden
      sx={(theme) => ({
        width: 40,
        height: 40,
        borderRadius: `${String(radius.control)}px`,
        display: 'grid',
        placeItems: 'center',
        bgcolor: alpha(theme.palette.tokens.textOnPrimary, 0.18),
      })}
    >
      {busy ? <CircularProgress size={18} color="inherit" /> : <Icon icon={ArrowRight} size={18} />}
    </Box>
  </ButtonBase>
);
